import React, { FC, useRef, useState } from 'react';
import FileUploadIndicator from '@/components/FileUploadIndicator';
import Buttons from '@/components/Buttons';
import { PlusCircle } from '@phosphor-icons/react/dist/ssr';

import { nanoid } from 'nanoid';
import { Upload } from 'tus-js-client';
import { QUESTIONNAIRES_BACKEND_UPLOAD_HOST } from '@configuration/client';
import { useUser } from '@providers/Auth';
import { mixpanelTrack } from '@/providers/Mixpanel';
import slugify from '@/helpers/slugify';
import classNames from 'classnames';

import useNoodleApi from '@/hooks/useNoodleApi';
import * as tsClient from '@tsClient';
import { FormProviders } from '@typings/api-models';
import ProgressIndicator from '@/components/ProgressIndicator';
import { ApiDefinition } from '@noodleApi';
import s from './CreatePdfInferenceRequest.module.scss';

type Props = {
  title: string;
  provider: FormProviders;
  creatorId: string;
  questionnaireSlug?: string;
  onSuccess: () => void;
  onError: () => void;
  compact?: boolean;
};

const CreatePdfInferenceRequest: FC<Props> = ({
  title,
  provider,
  creatorId,
  questionnaireSlug,
  onError,
  onSuccess,
  compact,
}) => {
  const [user] = useUser();
  const token = user?.token;

  const [uploadPercentage, setUploadPercentage] = useState(0);
  const [uploading, setUploading] = useState(false);

  const isSubmitDisabled = !title;

  const inputRef = useRef<HTMLInputElement>(null);

  const { getData: postTemplate } = useNoodleApi(tsClient.formRequests.postFormRequestTemplate);

  type FormTemplateResponseType = ApiDefinition['/form-request-templates']['POST']['response'];

  const createFormTemplate = async (): Promise<FormTemplateResponseType | null> => {
    const res = await postTemplate({
      canAutoComplete: false,
      creatorId,
      description: '',
      enableAiSummary: true,
      inheritanceScheme: 'none',
      isFollowupEnabled: false,
      name: title,
      provider,
      providerLocator: '',
    });
    return res.data;
  };

  const handleChange = async (e: React.FormEvent<HTMLInputElement>): Promise<void> => {
    setUploading(true);
    const target = e.target as HTMLInputElement;
    const files = target.files && target.files;
    if (!files) {
      return;
    }

    const headers: ConstructorParameters<typeof Upload>[1]['headers'] = {};
    if (token) {
      headers.authorization = token;
    }
    let formRequest: FormTemplateResponseType | null;

    if (!questionnaireSlug) {
      formRequest = await createFormTemplate();
    }

    Array.from(files).forEach(file => {
      mixpanelTrack('CreatePDFInferenceRequestModal - file added', { fileName: file.name, fileType: file.type });

      const slug = slugify({ name: file.name });

      const upload = new Upload(file, {
        endpoint: `${QUESTIONNAIRES_BACKEND_UPLOAD_HOST}/uploads`,
        headers,
        metadata: {
          contentType: 'application/pdf',
          creatorId,
          description: file.name,
          name: file.name,
          questionnaireSlug: questionnaireSlug || formRequest?.providerLocator || '',
          referenceId: nanoid(),
          referenceType: 'form-request',
          slug,
        },
        onError: () => {
          setUploading(false);
          onError();
          setUploadPercentage(0);
        },
        onProgress: (bytesUploaded, bytesTotal) => {
          const percentage = (bytesUploaded / bytesTotal) * 100;
          setUploadPercentage(percentage);
        },
        onSuccess: () => {
          setUploading(false);
          onSuccess();
          setUploadPercentage(0);
        },
        retryDelays: [0, 3000, 5000, 10000, 20000],
      });
      upload.start();
    });
  };

  const handleClick = (): void => {
    if (!isSubmitDisabled) inputRef?.current?.click();
  };

  return (
    <div className={s.modal}>
      {uploading
        ? (
          <div className={s['progress-indicator-container']}>
            <ProgressIndicator size={24} />
          </div>
        )
        : (
          <>
            <Buttons isWrapper disabled={isSubmitDisabled} onClick={handleClick} className={classNames(compact ? s['pdf-container-compact'] : s['pdf-container'])}>
              <input ref={inputRef} type={'file'} onChange={handleChange} className={s['pdf-input']} multiple />
              {uploadPercentage
                ? (
                  <div className={s['upload-wrapper']}>
                    <FileUploadIndicator uploadPercentage={uploadPercentage} />
                  </div>
                )
                : (
                  <PlusCircle size={compact ? 16 : 28} weight="fill" />
                )}
              <p className="caption">
                {isSubmitDisabled ? 'Please add form request title before file upload' : <>{uploadPercentage ? '' : 'Upload Files'}</>}
              </p>
            </Buttons>
          </>
        )}
    </div>
  );
};

export default CreatePdfInferenceRequest;
